import { useCallback, useEffect, useRef, useState } from "react";
import { Group, Layer, Stage } from "react-konva";
import { Spinner } from "react-bootstrap";
import { useSelector, shallowEqual } from "react-redux";
import { ILocation } from "../../interface/ILocation";
import SeatPopup from "./SeatPopup";
import { SectorBlock } from "./Sector";
import { StaticObject, TextObject } from "./StaticObjects";
import { IDataHover } from "../../interface/DataHover";
import { ObjData, StaticObj, TextObj } from "../../interface/StaticObject";
import { Point } from "../../interface/Point";
import ShemeCss from './ShemeStyle.module.css'
import { ICategoryAssignments } from "../../interface/ICategoryAssignments";
import { IBasket } from "../../interface/IBasket";

// Константы
const MIN_SCALE = 0.25;
const MAX_SCALE = 1.5;
const SCALE_BY = 1.06;
const HEIGHT_BOX = 1000;

interface StageSize {
    width: number;
    height: number;
}

interface MainStageProps {
    cursorPos: Point;
    setCursorPos: (pos: Point) => void;
    clickAdd?: boolean | null;
    setClickAdd?: (clickAdd: boolean) => void;
    soldOut?: boolean | null;
    handleSelect: (selectItem: IBasket) => void;
    handleDeselect: (selectItem: IBasket) => void;
    showPaid?: boolean;
    editorMode?: boolean;
}

// Вспомогательные функции
const getDistance = (p1: Point, p2: Point): number =>
    Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

const getCenter = (p1: Point, p2: Point): Point => ({
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2
});

const clampScale = (scale: number): number =>
    Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));

const parseNumber = (value: string | number): number =>
    typeof value === 'string' ? parseInt(value, 10) : value;

export const MainStage: React.FC<MainStageProps> = ({
    setCursorPos,
    soldOut,
    handleSelect,
    handleDeselect,
    showPaid = false,
    editorMode = false
}) => {
    // Refs
    const stageRef = useRef<any>(null);
    const layerRef = useRef<any>(null);
    const groupRef = useRef<any>(null);

    // State
    const [position, setPosition] = useState<Point>({ x: 0, y: 0 });
    const [lastCenter, setLastCenter] = useState<Point>();
    const [lastDist, setLastDist] = useState(0);
    const [isScaling, setIsScaling] = useState(false);
    const [scale, setScale] = useState(1);
    const [containerSize, setContainerSize] = useState<StageSize>({ width: 0, height: 0 });
    const [virtualSize, setVirtualSize] = useState<StageSize>({ width: 0, height: 0 });
    const [texts, setTexts] = useState<TextObj[]>([]);
    const [staticObjects, setStaticObjects] = useState<StaticObj[]>([]);
    const [popup, setPopup] = useState<IDataHover | null>(null);

    // Redux state
    const locationData = useSelector((state: any) => state.locationScheme, shallowEqual) as ILocation;
    const basketInStore = useSelector((state: any) => state.basket, shallowEqual);
    const sessionAssignments = useSelector((state: any) => state.sessionAssignments, shallowEqual) as ICategoryAssignments[];

    // Обработчик контейнера
    const containerRef = useCallback((node: HTMLDivElement | null) => {
        if (node) {
            setContainerSize({
                width: node.offsetWidth,
                height: node.offsetHeight
            });
        }
    }, []);

    // Расчет масштаба и позиции
    const setScaleAndPosition = useCallback((newScale: number) => {
        const clampedScale = clampScale(newScale);
        const x = containerSize.width / 2 - (virtualSize.width / 2 * clampedScale);
        const y = containerSize.height / 2 - (virtualSize.height / 2 * clampedScale);

        setPosition({ x, y });
        setScale(clampedScale);
    }, [containerSize, virtualSize]);

    // Инициализация масштаба при изменении размеров
    useEffect(() => {
        if (virtualSize.height > 0 && virtualSize.width > 0 &&
            containerSize.width > 0 && containerSize.height > 0) {

            const containerAspectRatio = containerSize.width / containerSize.height;
            const virtualAspectRatio = virtualSize.width / virtualSize.height;

            let scaleToFit: number;
            let horizontalPadding = 0;

            // Если контейнер вертикальный, а схема горизонтальная
            if (containerAspectRatio < 1 && virtualAspectRatio > 1) {
                // Добавляем отступы по бокам (например, 10% с каждой стороны)
                const horizontalPaddingPercent = 0.1;
                horizontalPadding = containerSize.width * horizontalPaddingPercent;

                const availableWidth = containerSize.width - (horizontalPadding * 2);
                scaleToFit = Math.min(
                    availableWidth / virtualSize.width,
                    containerSize.height / virtualSize.height
                );
            }
            // Если контейнер горизонтальный, а схема вертикальная
            else if (containerAspectRatio > 1 && virtualAspectRatio < 1) {
                // Добавляем отступы сверху и снизу (например, 10%)
                const verticalPaddingPercent = 0.1;
                const verticalPadding = containerSize.height * verticalPaddingPercent;

                const availableHeight = containerSize.height - (verticalPadding * 2);
                scaleToFit = Math.min(
                    containerSize.width / virtualSize.width,
                    availableHeight / virtualSize.height
                );
            }
            // Обычный расчет
            else {
                scaleToFit = Math.min(
                    containerSize.width / virtualSize.width,
                    containerSize.height / virtualSize.height
                );
            }

            // Дополнительно можно применить минимальный масштаб для вертикальных схем
            if (virtualAspectRatio < 0.8) { // Если схема сильно вертикальная
                const minScaleForVertical = Math.min(
                    (containerSize.width * 0.8) / virtualSize.width, // Используем 80% ширины
                    containerSize.height / virtualSize.height
                );
                scaleToFit = Math.max(scaleToFit, minScaleForVertical);
            }

            setScaleAndPosition(scaleToFit);
        }
    }, [containerSize, virtualSize, setScaleAndPosition]);

    // Парсинг объектов локации
    useEffect(() => {
        if (!locationData) return;

        const newObjects: StaticObj[] = [];
        const newTexts: TextObj[] = [];

        locationData.staticObjects.forEach((obj: ObjData) => {
            const data = JSON.parse(obj.objectData) as any;
            if (!data) return;

            data.id = obj.id;

            if (data.type === "object") {
                // Нормализация данных объекта
                data.x = parseNumber(data.x);
                data.y = parseNumber(data.y);
                data.width = parseNumber(data.width);
                data.strokeWidth = parseNumber(data.strokeWidth);
                data.zIndex = data.zIndex === undefined ? 0 : parseNumber(data.zIndex);

                // Определение рабочей области
                if (data.name === "Рабочая область" && data.ctxs?.[0]) {
                    setVirtualSize({
                        width: parseNumber(data.ctxs[0].x),
                        height: parseNumber(data.ctxs[0].y)
                    });
                }

                newObjects.push(data as StaticObj);
            }

            if (data.type === "text") {
                newTexts.push(data as TextObj);
            }
        });

        // Сортировка объектов по zIndex
        if (newObjects.length > 0) {
            const sortedObjects = [...newObjects].sort((a, b) => a.zIndex - b.zIndex);
            setStaticObjects(sortedObjects);
        }

        setTexts(newTexts);
    }, [locationData]);

    // Обработчики жестов
    const handleTouchStart = useCallback((event: { evt: TouchEvent }) => {
        const touches = event.evt.touches;
        if (touches.length === 2) {
            setIsScaling(true);

            const p1: Point = { x: touches[0].clientX, y: touches[0].clientY };
            const p2: Point = { x: touches[1].clientX, y: touches[1].clientY };

            setLastCenter(getCenter(p1, p2));
            setLastDist(getDistance(p1, p2));
        }
    }, []);

    const handleTouchMove = useCallback((event: { evt: TouchEvent }) => {
        if (!isScaling || !lastCenter || !stageRef.current) return;

        const touches = event.evt.touches;
        if (touches.length === 2) {
            const p1: Point = { x: touches[0].clientX, y: touches[0].clientY };
            const p2: Point = { x: touches[1].clientX, y: touches[1].clientY };

            const newCenter = getCenter(p1, p2);
            const dist = getDistance(p1, p2);

            const pointTo = {
                x: (newCenter.x - stageRef.current.attrs.x) / stageRef.current.attrs.scaleX,
                y: (newCenter.y - stageRef.current.attrs.y) / stageRef.current.attrs.scaleX,
            };

            const newScale = clampScale(stageRef.current.attrs.scaleX * (dist / lastDist));
            const dx = newCenter.x - lastCenter.x;
            const dy = newCenter.y - lastCenter.y;

            const newPos = {
                x: newCenter.x - pointTo.x * newScale + dx,
                y: newCenter.y - pointTo.y * newScale + dy,
            };

            setLastDist(dist);
            setLastCenter(newCenter);
            setScale(newScale);
            setPosition(newPos);
        }
    }, [isScaling, lastCenter, lastDist]);

    const handleTouchEnd = useCallback(() => {
        setIsScaling(false);
    }, []);

    // Обработчик колеса мыши
    const handleWheel = useCallback((e: any) => {
        e.evt.preventDefault();

        const stage = stageRef.current;
        if (!stage) return;

        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        const mousePointTo = {
            x: (pointer.x - stage.x()) / oldScale,
            y: (pointer.y - stage.y()) / oldScale,
        };

        let direction = e.evt.deltaY > 0 ? 1 : -1;
        if (e.evt.ctrlKey) {
            direction = -direction;
        }

        const newScale = clampScale(direction > 0 ? oldScale * SCALE_BY : oldScale / SCALE_BY);

        setScale(newScale);
        setPosition({
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale
        });
    }, []);

    // Обработчик наведения
    const handleHover = useCallback((dataHover: IDataHover) => {
        if (!dataHover) {
            setPopup(null);
            return;
        }

        if (!locationData || !sessionAssignments) return;

        const enrichedData = { ...dataHover };

        // Обработка мест (assignmentType === 3)
        if (enrichedData.assignmentType === 3) {
            for (const sector of locationData.sectors) {
                if (!sector?.seats?.length) continue;

                const seat = sector.seats.find(seat => seat.id === enrichedData.id);
                if (seat) {
                    const hoverAssignment = sessionAssignments.find(item => item.targetId === seat.id);
                    if (hoverAssignment) {
                        if (!enrichedData.orderId) {
                            enrichedData.price = hoverAssignment.price;
                        }
                        enrichedData.categoryFill = hoverAssignment.color;
                        enrichedData.category = hoverAssignment.name;
                        enrichedData.groupTag = hoverAssignment.groupTag;
                    }
                    break;
                }
            }
        }

        // Обработка секторов (assignmentType === 2)
        if (enrichedData.assignmentType === 2) {
            const hoverAssignment = sessionAssignments.find(item => item.targetId === enrichedData.id);
            if (hoverAssignment) {
                enrichedData.price = hoverAssignment.price;
                enrichedData.categoryFill = hoverAssignment.color;
                enrichedData.category = hoverAssignment.name;
            }
        }

        // Обработка групповых тегов
        if (enrichedData.groupTag) {
            const groupAssignments = sessionAssignments.filter(item => item.groupTag === enrichedData.groupTag);
            enrichedData.quantitySeat = groupAssignments.length;
            enrichedData.price = groupAssignments.reduce((sum, item) => sum + item.price, 0);
        }

        setPopup(enrichedData);
    }, [locationData, sessionAssignments]);

    // Обработчик позиции курсора
    const handleCursor = useCallback((pos: Point) => {
        setCursorPos(pos);
    }, [setCursorPos]);


    const handleDeskTopDragMove = (e: any) => {
        const currentScale = scale;

        // Коэффициент увеличения границ - чем больше scale, тем больше границы
        // Например, при scale=2 границы будут в 2-3 раза больше
        const scaleFactor = currentScale * 1.5; // Можете регулировать множитель (1.5, 2, и т.д.)

        // Используем размер виртуального пространства или контейнера
        const boundsWidth = virtualSize.width || window.innerWidth;
        const boundsHeight = virtualSize.height || window.innerHeight;

        // Границы увеличиваются пропорционально scale^2 для более естественного поведения
        const maxOffsetX = boundsWidth * 0.5 * scaleFactor;
        const maxOffsetY = boundsHeight * 0.5 * scaleFactor;

        const posX = e.target.absolutePosition().x;
        const posY = e.target.absolutePosition().y;

        // Применяем ограничения
        if (Math.abs(posX) > maxOffsetX) {
            const direction = posX > 0 ? 1 : -1;
            const limitedX = maxOffsetX * direction;
            e.target.x(limitedX);
        }

        if (Math.abs(posY) > maxOffsetY) {
            const direction = posY > 0 ? 1 : -1;
            const limitedY = maxOffsetY * direction;
            e.target.y(limitedY);
        }
    }

    // Загрузка
    if (!locationData) {
        return <Spinner />;
    }

    return (
        <div
            ref={containerRef}
            style={{
                position: "relative",
                backgroundColor: "#fff",
                width: "100%",
                height: HEIGHT_BOX,
            }}
            className={`${ShemeCss.shemeBox} shadow`}
            id="schemeHellcontainer"
        >
            {soldOut && (
                <div
                    className="position-absolute d-grid justify-content-center align-content-center"
                    style={{ zIndex: 999, height: '100%', width: '100%' }}
                >
                    <img
                        src="/sold_out.png"
                        alt="Sold Out"
                        className="sold_out"
                        style={{  }}
                    />
                </div>
            )}

            <Stage
                ref={stageRef}
                width={containerSize.width}
                height={containerSize.height}
                draggable={!isScaling}
                scaleX={scale}
                scaleY={scale}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDragMove={handleDeskTopDragMove}
                onMouseMove={(e) => {
                    const pos = e.currentTarget.getRelativePointerPosition() as Point;
                    if (pos) handleCursor(pos);
                }}
                onWheel={handleWheel}
                x={position.x}
                y={position.y}
            >
                <Layer ref={layerRef}>
                    <Group ref={groupRef}>
                        {staticObjects.map((staticObject) => (
                            <StaticObject key={staticObject.id} object={staticObject} />
                        ))}

                        {texts.map((text) => (
                            <TextObject key={text.id} text={text} />
                        ))}

                        {locationData.sectors?.map(sector => (
                            <SectorBlock
                                key={sector.id}
                                sectorData={sector}
                                selectedSeatsIds={basketInStore}
                                onHoverSeat={handleHover}
                                onSelectSeat={handleSelect}
                                onDeselectSeat={handleDeselect}
                                sessionAssignments={sessionAssignments}
                                showPaid={showPaid}
                                editorMode={editorMode}
                            />
                        ))}
                    </Group>
                </Layer>
            </Stage>

            {popup?.cursor && (
                <SeatPopup
                    dataHover={popup}
                    onClose={() => setPopup(null)}
                />
            )}
        </div>
    );
};